import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useQuery } from 'react-query';
import {
  CartesianGrid,
  DotProps,
  Line,
  LineChart,
  ReferenceLine,
  XAxis,
  YAxis,
} from 'recharts';

import {
  parseParamsTshirtAndShield,
  tshirtKeys,
} from '../edit-my-account/functions/utils';

import { formatDni } from '.';

import { IconCaptain } from '@/components/Icons';
import { IconInsured } from '@/components/Icons/IconInsured';
import { TshirtTeam } from '@/components/TshirtTeam';
import * as Chart from '@/components/chart';
import useAuth from '@/hooks/useAuth';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import {
  getCalificacionesHistoricas,
  getEquipoGanador,
} from '@/services/ranking';
import { initialTshitAndShield } from '@/store/tshirtAndShield';
import {
  DatosUsuarioPosicionTO,
  RankingTO,
  ResponseRankingEquipoGanador,
} from '@/types/ranking';
import {
  cn,
  formatMillionBalance,
  formatNumberToLocale,
  measureTextWidth,
  truncateToWidth,
} from '@/utils';

const millify = (n: string): string => {
  const nx = Number(n.replace(/\./g, ''));
  if (nx < 1e3) return `${nx}`;

  const units = 'KMBT';
  const exponent = (Math.log10(nx) / 3) | 0;
  const shortNum = (nx / 1e3 ** exponent).toFixed(1).replace(/\.0$/, '');

  return `${shortNum}${units[exponent - 1]}`;
};

interface ModalWinnerProps {
  DT: RankingTO | null;
  isF5: boolean;
  isDate: boolean;
}

const Jugador = ({
  jugador,
}: {
  jugador: ResponseRankingEquipoGanador['jugador'][0];
}) => {
  const [lastname, name] = jugador.nombre.split(',').map((s) => s.trim());
  const suffix = name ? `, ${name}` : '';
  const nameRef = useRef<HTMLDivElement>(null);
  const [displayLastname, setDisplayLastname] = useState(lastname);

  useLayoutEffect(() => {
    const el = nameRef.current;
    if (!el) return;

    const update = () => {
      const font = window.getComputedStyle(el).font;
      const suffixWidth = suffix ? measureTextWidth(suffix, font) : 0;
      const availableWidth = Math.max(0, el.clientWidth - suffixWidth);
      setDisplayLastname(truncateToWidth(lastname, availableWidth, font));
    };

    update();

    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, [lastname, suffix]);

  return (
    <div className="bg-white lg:py-4 lg:pl-4 py-2 pl-2">
      <div className="flex items-center justify-between w-full md:justify-start">
        <div className="flex flex-1 items-baseline min-w-0 lg:text-base text-sm font-bold">
          <div
            ref={nameRef}
            className="min-w-0 overflow-hidden whitespace-nowrap"
          >
            {displayLastname}
            {suffix}
          </div>
          <div className="flex items-center justify-center ml-1">
            <div className="flex justify-center items-center">
              <p className="text-gdt-gray-4 font-semibold text-xs">
                {jugador.posicion}
              </p>
            </div>
          </div>
          <div className="flex space-x-1 ml-1">
            {jugador.capitan === 'true' && (
              <IconCaptain className="bg-gdt-blue text-white border-gdt-blue" />
            )}
            {jugador.asegurado === 'true' && (
              <IconInsured className="bg-gdt-blue-light2 text-gdt-blue" />
            )}
          </div>
        </div>
        <div className="w-[70px] text-center">
          <p className=" text-gdt-gray-4 font-semibold leading-[inherit]">
            ${millify(jugador.cotizacion)}
          </p>
        </div>
        <div className="w-20 text-center">
          <p className=" text-gdt-gray-3 font-semibold text-center">
            {jugador.puntos ?? '-'}
          </p>
        </div>
      </div>
    </div>
  );
};

export const ContentModalWinner = ({ DT, isF5, isDate }: ModalWinnerProps) => {
  const { userInitialData } = useAuth();
  const [data, setData] = useState<ResponseRankingEquipoGanador | null>(null);
  const { isMobile } = useIsDesktop();

  useQuery(
    'equipoGanador',
    async () =>
      getEquipoGanador(
        userInitialData?.id_ult_fecha_puntos.toString() ?? '1111',
        isF5,
        isDate
      ),
    {
      onSuccess(data) {
        const selectedDT = Array.isArray(data)
          ? data.find((e) => e.equipo === DT?.equipo)
          : data;
        setData(selectedDT ?? null);
      },
    }
  );

  const tshirt = useMemo(() => {
    return parseParamsTshirtAndShield(
      DT?.camisetaSvg,
      tshirtKeys,
      initialTshitAndShield.tshirt
    );
  }, [DT]);

  const parseMoney = (value: string | number) => {
    if (typeof value === 'number') return value;
    return Number(value.replace(/\./g, '').replace(',', '.'));
  };

  const formatName = useCallback(
    (nombre: string) => {
      if (!isMobile) return nombre;

      const [lastname, name] = nombre.split(',').map((s) => s.trim());
      if (!name) return nombre;

      return `${lastname}, ${name.charAt(0)}.`;
    },
    [isMobile]
  );

  const titulares = useMemo(() => {
    const jugadores = isF5
      ? data?.jugador
      : data?.jugador.filter((e) => e.titular === 'true') ?? [];

    return jugadores?.map((e) => ({ ...e, nombre: formatName(e.nombre) }));
  }, [isF5, data, formatName]);

  const suplentes = useMemo(() => {
    if (isF5) {
      return [];
    }
    return (
      data?.jugador
        .filter((e) => e.titular === 'false')
        .map((e) => ({ ...e, nombre: formatName(e.nombre) })) ?? []
    );
  }, [isF5, data, formatName]);

  const totalBalance = useMemo(() => {
    if (!data?.jugador) return 0;

    const result = data?.jugador.reduce(
      (acc, p) => acc + parseMoney(p.cotizacion || 0),
      0
    );

    return formatMillionBalance(result);
  }, [data?.jugador]);

  if (!DT) {
    return null;
  }

  return (
    <div className="flex flex-col gap-y-4 mt-4">
      <div className="flex justify-between items-start bg-white rounded-lg p-4">
        <div className="mx-4">
          <figure className="my-auto w-full max-w-9 mt-1">
            <TshirtTeam
              tshirt={tshirt}
              withShield={
                JSON.stringify(tshirt) ===
                JSON.stringify(initialTshitAndShield.tshirt)
              }
            />
          </figure>
        </div>
        <div className="flex-1 lg:mr-4">
          <div className="flex justify-between items-start">
            <div className="flex flex-col flex-1">
              <h2 className="font-bold text-sm">{data?.nombre}</h2>
              <div className="flex flex-1 justify-between">
                <p>{data?.equipo}</p>
                <p className="font-bold text-sm text-gdt-blue ml-2 min-w-[55px] text-end">
                  {DT.puntos} {!isMobile ? 'Puntos' : 'pts.'}
                </p>
              </div>
            </div>
          </div>
          <div className="flex text-gdt-gray-2 flex-1 justify-between lg:flex-row flex-col">
            <p>{formatDni(data?.dni ?? 'XXX.XXX.XXX')}</p>
            <p>Cotiz. total: ${totalBalance}</p>
          </div>
        </div>
      </div>

      <div
        className={cn(
          'flex flex-col gap-y-1 px-2 lg:px-0 h-96 overflow-y-auto',
          { 'h-auto': isF5 }
        )}
      >
        <div className="flex-1 flex text-xs font-semibold text-gdt-gray-4">
          <span className="flex-1 pl-4">Titulares</span>
          <span className="w-[70px] text-center">Cotización</span>
          <span className="w-20 text-center">Puntos</span>
        </div>
        <div className="space-y-1">
          {titulares?.map((jugador, i) => (
            <Jugador key={i} jugador={jugador} />
          ))}
          {suplentes.length > 0 && (
            <div className="flex-1 flex text-sm font-semibold text-gdt-gray-4 mt-2">
              <span className="pl-4">Suplentes</span>
            </div>
          )}
          <div className="flex flex-col gap-y-1">
            {suplentes.map((jugador, i) => (
              <Jugador key={i} jugador={jugador} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const chartConfig = {
  position: {
    color: 'hsla(225, 100%, 60%, 1)',
  },
} satisfies Chart.ChartConfig;

const CustomizedDot = ({ cx, cy, stroke }: DotProps) => {
  if (cx === undefined || cy === undefined || stroke === undefined) {
    return null;
  }
  return (
    <svg height={10} width={10} x={cx - 5} y={cy - 5}>
      <circle
        cx={5}
        cy={5}
        fill={stroke}
        fillOpacity={1}
        r={3}
        stroke={stroke}
        strokeOpacity={0.5}
        strokeWidth={4}
      />
    </svg>
  );
};

const CustomTooltipContent = ({ payload }: any) => {
  if (payload?.length) {
    const p1 = payload[0].value
      ? `${formatNumberToLocale(payload[0].value)} Pts`
      : '-';
    const p2 = payload[1].value
      ? `${formatNumberToLocale(payload[1].value)} Pts`
      : '-';
    return (
      <div className="flex flex-col gap-y-1">
        <div className="bg-gdt-status-green font-bold text-2xs text-white px-2 py-1 rounded-full">
          {p2}
        </div>
        <div className="bg-gdt-status-red font-bold text-2xs text-white px-2 py-1 rounded-full">
          {p1}
        </div>
      </div>
    );
  }
  return null;
};

interface ModalChartProps {
  selectedWinner: RankingTO | null;
  isF5?: boolean;
  // f5UserData?: ResponseInitMyF5;
  // f5RankingData: InfoRanking;
  // f11RankingData: InfoRanking;
  isGeneralTournament?: boolean;
}

/**
 * Modal de comparación de puntajes entre el usuario y el ganador del torneo/fecha.
 * Gráfico de líneas con las calificaciones de ambos usuarios.
 */
export const ModalChart = ({
  selectedWinner,
  isF5,
  // f5UserData,
  // f5RankingData,
  // f11RankingData,
  isGeneralTournament,
}: ModalChartProps) => {
  const [data, setData] = useState<Record<string, any>[]>([]);
  const [calificaciones, setCalificaciones] = useState<
    DatosUsuarioPosicionTO[]
  >([]);
  const { userInitialData } = useAuth();
  const idUsuarioDT = selectedWinner?.idUsuarioDT ?? 0;

  useEffect(() => {
    const fetchData = async () => {
      const data = await getCalificacionesHistoricas(
        Number(idUsuarioDT),
        isF5 ? 2 : 1
      );
      setData(
        data.calificacionesGrafico?.datos?.map((e, i) => ({
          name: data.calificacionesGrafico?.ejeX?.[i] ?? '',
          p1: e[userInitialData?.idUsuarioDT ?? 0] ?? 0,
          p2: e[idUsuarioDT] ?? 0,
        })) ?? []
      );
      setCalificaciones(
        data?.calificaciones?.map((player) => {
          // torneo general
          if (isGeneralTournament) {
            // jugador numero 1
            /*
            if (i === 1) {
              // si es el jugador 1, le asigno los puntos del torneo general f5
              if (isF5) {
                return {
                  ...player,
                  ptosFecha: player?.ptosAcumulados?.toString() ?? '0',
                  posFecha: '1',
                };
              }
              return {
                ...player,
                ptosFecha: player?.ptosAcumulados?.toString(),
                posFecha: '1',
              };
            }
            // jugador usuario
            
            if (isF5) {
              return {
                ...player,
                ptosFecha: f5RankingData?.puntosGeneral?.toString() ?? '0',
                posFecha: f5RankingData?.posicionGeneral?.toString() ?? '-2',
              };
            }
              */
            return {
              ...player,
              ptosFecha: player?.ptosAcumulados?.toString() ?? '0',
              posFecha: player?.posGral?.toString() ?? '-2',
            };
          } else {
            return player;
          }
          // torneo fecha
          /* 
          if (i === 1) {
            return {
              ...player,
              posFecha: '1',
            };
          }
          if (isF5) {
            return {
              ...player,
              ptosFecha: f5RankingData?.puntosFecha?.toString() ?? '0',
              posFecha: f5RankingData?.posicionFecha?.toString() ?? '-2',
            };
          } */
        }) ?? []
      );
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Chart.ChartContainer config={chartConfig}>
        <LineChart
          data={data}
          height={300}
          margin={{ bottom: 10, top: 10, right: 10 }}
          width={500}
        >
          <CartesianGrid horizontal strokeDasharray="2 2" vertical={false} />
          <XAxis
            axisLine={{
              strokeWidth: 0.6,
            }}
            dataKey="name"
            label={{
              value: 'FECHA',
              position: 'insideBottom',
              style: {
                textAnchor: 'middle',
                fontWeight: 'bold',
                fontSize: 14,
                fill: '#000000',
              },
              offset: -8,
            }}
          />
          <YAxis
            axisLine={false}
            domain={['dataMin - 20', 'dataMax + 1']}
            interval={0}
            label={{
              value: 'PUNTOS',
              angle: -90,
              position: 'insideLeft',
              style: {
                textAnchor: 'middle',
                fontWeight: 'bold',
                fontSize: 14,
                fill: '#000000',
              },
              offset: 10,
            }}
            tickLine={false}
          />
          <ReferenceLine
            label={{
              value: '0',
              position: 'left',
              fill: '#747474',
              fontSize: 12,
            }}
            stroke="#e0e0e0"
            strokeDasharray="2 2"
            y={0}
          />
          <Chart.ChartTooltip
            content={<CustomTooltipContent />}
            cursor={false}
          />
          <Line
            dataKey="p1"
            dot={<CustomizedDot />}
            stroke="#D84638"
            strokeLinejoin="miter"
            strokeWidth={2}
            type="linear"
          />
          <Line
            dataKey="p2"
            dot={<CustomizedDot />}
            stroke="#267F5F"
            strokeWidth={2}
            type="linear"
          />
        </LineChart>
      </Chart.ChartContainer>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-5">
        {calificaciones.map((calificacion, index) => (
          <div
            key={calificacion.nombreEquipo}
            className={`flex flex-col gap-y-2 p-4 rounded-lg md:rounded-none ${
              !index ? 'bg-gdt-status-red-light' : ' bg-gdt-status-green-light'
            }`}
          >
            <div className="flex items-center gap-x-2">
              <div className="w-3 h-3">
                <svg height={10} width={10}>
                  <circle
                    cx={5}
                    cy={5}
                    fill={!index ? '#D84638' : '#267F5F'}
                    fillOpacity={1}
                    r={3}
                    stroke={!index ? '#D84638' : '#267F5F'}
                    strokeOpacity={0.5}
                    strokeWidth={4}
                  />
                </svg>
              </div>
              <span className="text-sm font-bold">
                {calificacion.nombreUsuarioDT}
              </span>
            </div>
            <span className="text-gdt-gray-2 text-xs font-semibold w-full border-b border-white pb-2">
              {calificacion.nombreEquipo}
            </span>
            <div className="flex flex-col ">
              <span
                className={`${
                  !index ? 'text-gdt-status-red' : 'text-gdt-status-green'
                } font-bold text-sm`}
              >
                {formatNumberToLocale(calificacion.ptosFecha ?? '0')} Ptos
              </span>
              <span className="text-gdt-gray-3 text-sm">
                Puesto {formatNumberToLocale(calificacion.posFecha ?? '0')} en{' '}
                {isGeneralTournament ? 'el torneo' : 'la fecha'}
              </span>
            </div>
          </div>
        ))}
      </div>
    </>
  );
};
